// /admin/conferences — org-wide visibility into parent-teacher conferences.
//
// Read-mostly with override controls (admin can cancel any slot or booking
// regardless of teacher/parent ownership — RLS allows it).

import { useEffect, useMemo, useState } from "react";
import {
  CalendarBlank,
  Clock,
  CheckCircle,
  XCircle,
  Funnel,
  ArrowsClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  cancelBooking,
  cancelSlot,
  formatSlotRange,
  listOrgSlotsForDateRange,
} from "@/lib/conferences";

function isoDay(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

const RANGES = [
  { key: "7d", label: "Next 7 days", days: 7 },
  { key: "14d", label: "Next 14 days", days: 14 },
  { key: "30d", label: "Next 30 days", days: 30 },
  { key: "all", label: "All upcoming", days: 365 },
];

export default function AdminConferences() {
  const [rangeKey, setRangeKey] = useState("14d");
  const [classroomFilter, setClassroomFilter] = useState("all");
  const [teacherFilter, setTeacherFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const range = RANGES.find((r) => r.key === rangeKey) || RANGES[1];
      const fromIso = new Date(`${isoDay(0)}T00:00:00`).toISOString();
      const toIso = new Date(`${isoDay(range.days)}T23:59:59`).toISOString();
      const rows = await listOrgSlotsForDateRange({ fromIso, toIso });
      setSlots(rows || []);
    } catch (err) {
      toast.error(err.message || "Could not load conferences");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeKey]);

  const classrooms = useMemo(() => {
    const m = new Map();
    for (const s of slots) {
      if (s.classroom?.id && !m.has(s.classroom.id)) m.set(s.classroom.id, s.classroom);
    }
    return Array.from(m.values());
  }, [slots]);

  const teachers = useMemo(() => {
    const m = new Map();
    for (const s of slots) {
      if (s.teacher?.id && !m.has(s.teacher.id)) m.set(s.teacher.id, s.teacher);
    }
    return Array.from(m.values());
  }, [slots]);

  const filtered = useMemo(() => {
    return slots.filter((s) => {
      if (classroomFilter !== "all" && s.classroom?.id !== classroomFilter) return false;
      if (teacherFilter !== "all" && s.teacher?.id !== teacherFilter) return false;
      if (statusFilter !== "all") {
        const booked = s.bookings?.some((b) => b.status === "booked");
        if (statusFilter === "booked" && !booked) return false;
        if (statusFilter === "open" && (booked || s.status !== "open")) return false;
        if (statusFilter === "cancelled" && s.status !== "cancelled") return false;
      }
      return true;
    });
  }, [slots, classroomFilter, teacherFilter, statusFilter]);

  const kpis = useMemo(() => {
    const open = filtered.filter(
      (s) => s.status === "open" && !s.bookings?.some((b) => b.status === "booked"),
    ).length;
    const booked = filtered.filter((s) => s.bookings?.some((b) => b.status === "booked")).length;
    const cancelled = filtered.filter((s) => s.status === "cancelled").length;
    return { total: filtered.length, open, booked, cancelled };
  }, [filtered]);

  const onCancelSlot = async (slot) => {
    if (!window.confirm("Cancel this slot on the teacher's behalf?")) return;
    try {
      await cancelSlot(slot.id);
      toast.success("Slot cancelled");
      refresh();
    } catch (err) {
      toast.error(err.message || "Could not cancel");
    }
  };

  const onCancelBooking = async (booking) => {
    if (!window.confirm("Cancel this booking on the family's behalf?")) return;
    try {
      await cancelBooking(booking.id);
      toast.success("Booking cancelled");
      refresh();
    } catch (err) {
      toast.error(err.message || "Could not cancel");
    }
  };

  const grouped = useMemo(() => groupByDay(filtered), [filtered]);

  return (
    <div data-testid="admin-conferences" className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
            Operations
          </p>
          <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900">
            Conferences
          </h1>
          <p className="mt-1 max-w-xl text-sm text-neutral-600">
            Cross-classroom view of parent-teacher conference slots and bookings.
          </p>
        </div>
        <button
          type="button"
          data-testid="admin-conferences-refresh"
          onClick={refresh}
          className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50"
        >
          <ArrowsClockwise size={12} weight="bold" />
          Refresh
        </button>
      </div>

      <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi label="Slots in range" value={kpis.total} testId="admin-conferences-kpi-total" />
        <Kpi label="Open" value={kpis.open} testId="admin-conferences-kpi-open" />
        <Kpi label="Booked" value={kpis.booked} testId="admin-conferences-kpi-booked" />
        <Kpi label="Cancelled" value={kpis.cancelled} testId="admin-conferences-kpi-cancelled" />
      </section>

      <section className="rounded-2xl border border-neutral-200 bg-white p-4">
        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-neutral-500">
          <Funnel size={12} weight="duotone" />
          Filters
        </div>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-4">
          <FilterSelect
            testId="admin-conferences-range"
            label="Range"
            value={rangeKey}
            onChange={setRangeKey}
            options={RANGES.map((r) => ({ value: r.key, label: r.label }))}
          />
          <FilterSelect
            testId="admin-conferences-classroom"
            label="Classroom"
            value={classroomFilter}
            onChange={setClassroomFilter}
            options={[
              { value: "all", label: "All classrooms" },
              ...classrooms.map((c) => ({ value: c.id, label: c.name })),
            ]}
          />
          <FilterSelect
            testId="admin-conferences-teacher"
            label="Teacher"
            value={teacherFilter}
            onChange={setTeacherFilter}
            options={[
              { value: "all", label: "All teachers" },
              ...teachers.map((t) => ({ value: t.id, label: t.full_name || t.email })),
            ]}
          />
          <FilterSelect
            testId="admin-conferences-status"
            label="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: "all", label: "All" },
              { value: "open", label: "Open" },
              { value: "booked", label: "Booked" },
              { value: "cancelled", label: "Cancelled" },
            ]}
          />
        </div>
      </section>

      {loading && <p className="text-sm text-neutral-500">Loading…</p>}
      {!loading && filtered.length === 0 && (
        <p
          data-testid="admin-conferences-empty"
          className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50/30 px-3 py-6 text-center text-sm text-neutral-500"
        >
          No conference slots match the current filters.
        </p>
      )}

      <ul className="space-y-4" data-testid="admin-conferences-list">
        {grouped.map(([day, daySlots]) => (
          <li key={day}>
            <p className="mb-2 text-sm font-bold uppercase tracking-[0.15em] text-neutral-500">
              {day}
            </p>
            <ul className="space-y-2">
              {daySlots.map((s) => (
                <AdminSlotRow
                  key={s.id}
                  slot={s}
                  onCancelSlot={() => onCancelSlot(s)}
                  onCancelBooking={onCancelBooking}
                />
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Kpi({ label, value, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-neutral-200 bg-white p-4">
      <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">
        {label}
      </p>
      <p className="mt-1 font-heading text-2xl font-semibold text-neutral-900">{value}</p>
    </div>
  );
}

function FilterSelect({ testId, label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">
        {label}
      </span>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function groupByDay(slots) {
  const map = new Map();
  for (const s of slots) {
    const d = new Date(s.start_at);
    const label = d.toLocaleDateString(undefined, {
      weekday: "long",
      month: "short",
      day: "numeric",
    });
    if (!map.has(label)) map.set(label, []);
    map.get(label).push(s);
  }
  return Array.from(map.entries());
}

function AdminSlotRow({ slot, onCancelSlot, onCancelBooking }) {
  const past = new Date(slot.start_at) <= new Date();
  const booked = slot.bookings?.find((b) => b.status === "booked");

  let statusChip;
  if (slot.status === "cancelled") {
    statusChip = <Chip tone="error">Cancelled</Chip>;
  } else if (booked) {
    statusChip = <Chip tone="primary">Booked</Chip>;
  } else {
    statusChip = <Chip tone="muted">Open</Chip>;
  }

  return (
    <li
      data-testid={`admin-conference-slot-${slot.id}`}
      data-status={slot.status}
      data-booking-status={booked ? "booked" : "none"}
      className="rounded-2xl border border-neutral-200 bg-white p-3"
    >
      <div className="flex items-start gap-3">
        <Clock size={16} weight="duotone" className="mt-0.5 text-neutral-500" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-neutral-900">
            {formatSlotRange(slot.start_at, slot.end_at)}
          </p>
          <p className="text-[11px] text-neutral-500">
            {slot.teacher?.full_name || "Teacher"}
            {slot.classroom?.name ? ` · ${slot.classroom.name}` : ""}
            {slot.location ? ` · ${slot.location}` : ""}
          </p>
          {booked && (
            <p className="mt-1 text-[12px] text-neutral-700">
              Booked by{" "}
              {booked.family?.display_name || "family"}
              {booked.child?.first_name
                ? ` for ${booked.child.first_name}${booked.child.last_name ? " " + booked.child.last_name : ""}`
                : ""}
            </p>
          )}
        </div>
        {statusChip}
      </div>

      <div className="mt-2 flex flex-wrap items-center justify-end gap-1.5">
        {booked && !past && (
          <button
            type="button"
            data-testid={`admin-conference-cancel-booking-${slot.id}`}
            onClick={() => onCancelBooking(booked)}
            className="inline-flex items-center gap-1 rounded-full border border-[#C6851E]/40 bg-white px-2.5 py-1 text-[11px] font-semibold text-[#8A5A12] hover:bg-[#C6851E]/5"
          >
            <XCircle size={11} weight="duotone" />
            Cancel booking
          </button>
        )}
        {slot.status === "open" && !past && (
          <button
            type="button"
            data-testid={`admin-conference-slot-cancel-${slot.id}`}
            onClick={onCancelSlot}
            className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-50"
          >
            Cancel slot
          </button>
        )}
      </div>
    </li>
  );
}

function Chip({ tone, children }) {
  const toneClass = {
    primary: "bg-primary/10 text-primary-hover",
    ok: "bg-emerald-50 text-emerald-800",
    warn: "bg-amber-50 text-amber-800",
    error: "bg-[#C6851E]/10 text-[#8A5A12]",
    muted: "bg-neutral-100 text-neutral-700",
  }[tone || "muted"];
  return (
    <span
      className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${toneClass}`}
    >
      {children}
    </span>
  );
}
